import { saveHotLead } from 'actions/RecommendationActions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { useAppDispatch } from 'hooks/reduxHooks'
import { isBedbankType, isTourV2OfferType, isCruiseV1, isCruiseV2 } from 'lib/offer/offerTypes'
import { timeNowInSecond } from 'lib/datetime/time'
import { Moment } from 'moment'
import { useEffect, useMemo } from 'react'
import { isValidOccupancy } from 'lib/offer/occupancyUtils'

interface HotLeadParams {
  offerId?: string;
  offerType?: App.OfferType;
  checkInDate?: Moment,
  checkOutDate?: Moment,
  year?: number,
  month?: number,
  variant?: string,
  duration?: number,
  occupancy?: Array<App.RoomOccupants> | Array<App.Occupants>,
  defaultOccupancy?: Array<App.RoomOccupants>,
  holidayTypes?: Array<string>,
}
/**
 * Saves a hot lead to recommendations when it becomes valid
 * @param hotLeadData Data to determine/save hot lead
 * @returns Whether or not the data is valid hot lead or not
 */
export function useSaveHotLead(hotLeadData: HotLeadParams): App.HotLeadOffer | undefined {
  const {
    offerId,
    offerType,
    checkInDate,
    checkOutDate,
    duration,
    occupancy,
    defaultOccupancy,
    year,
    month,
    variant,
    holidayTypes,
  } = hotLeadData
  const isBedbank = isBedbankType(offerType)

  const dispatch = useAppDispatch()
  // for bedbank, a valid HL must contain dates (occupancy is selected by default)
  // for LE hotels, a valid HL only need user selecting non-default occupancy (dates optional)
  const validHotLead = useMemo(() => {
    if (!offerId || !offerType) { return false }

    const anyInvalidOccupancies = !occupancy || !isValidOccupancy(...occupancy)
    const hasValidDates = !!checkInDate && (!!duration || !!checkOutDate)
    if (isBedbank) {
      return hasValidDates && !anyInvalidOccupancies
    } else if (holidayTypes && isCruiseV1(offerType, holidayTypes)) {
      return !anyInvalidOccupancies && hasValidDates
    } else if (isCruiseV2(offerType)) {
      return !anyInvalidOccupancies && hasValidDates
    } else if (isTourV2OfferType(offerType)) {
      return !!year && !!month
    } else {
      return (occupancy !== defaultOccupancy) && !anyInvalidOccupancies && hasValidDates
    }
  }, [checkInDate, checkOutDate, defaultOccupancy, duration, isBedbank, month, occupancy, offerType, year, holidayTypes, offerId])

  const hotLead = useMemo(() => {
    if (!offerId || !offerType) { return undefined }

    if (validHotLead && (!!occupancy || isTourV2OfferType(offerType))) {
      const checkIn = checkInDate?.format(ISO_DATE_FORMAT)
      const checkOutMoment = duration ? checkInDate?.clone()?.add(duration, 'days') : checkOutDate
      const checkOut = checkOutMoment?.format(ISO_DATE_FORMAT)

      const newHotLead: App.HotLeadOffer = {
        offerId,
        offerType,
        checkIn,
        checkOut,
        occupancy: occupancy ?? [],
        year,
        month,
        variant,
        isDismissed: false,
        creationTime: timeNowInSecond(),
        category: 'sticky_hot_leads',
        lereVersion: 'hot_leads_local',
      }
      return newHotLead
    }
    return undefined
  }, [checkInDate, checkOutDate, duration, month, occupancy, offerId, offerType, validHotLead, variant, year])

  useEffect(() => {
    if (hotLead) {
      dispatch(saveHotLead(hotLead))
    }
  }, [dispatch, hotLead])

  return hotLead
}
